import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronUp, Check, ArrowRight, Gift } from 'lucide-react';
import { PRICING_DATA, Tier } from '../PricingSection';
import { STARTER_GRANT_USD, STARTER_GRANT_CREDITS, CREDITS_USED, DAILY_BURN, CONSUMPTION_MIX, WORKSPACE_PEOPLE, fmt } from './starterCreditPlan';

// ── Usage & billing · variant B ──────────────────────────────────────────────
// The starter-credit model: no trial, credit up front, subscribe once it runs
// out. Three account states, switchable for demos:
//
//   starter    — never subscribed, drawing down the free credit
//   subscribed — on a plan, second month, credits reset each cycle
//   depleted   — subscribed but out of credits mid-cycle
// ─────────────────────────────────────────────────────────────────────────────

type AccountState = 'starter' | 'subscribed' | 'depleted';
const STATES: {
  value: AccountState;
  label: string;
}[] = [{
  value: 'starter',
  label: 'Starter credit'
}, {
  value: 'subscribed',
  label: 'Subscribed'
}, {
  value: 'depleted',
  label: 'Out of credits'
}];
/** Currency symbol held as a value so template literals never swallow it. */
const USD = String.fromCharCode(36);
/** Formats a price with its currency symbol. */
const price = (n: number) => USD + fmt(n);
const TIERS = PRICING_DATA.filter((i): i is Tier => 'credits' in i);
/** The plan a subscribed workspace is already on. */
const CURRENT_PLAN = TIERS.find(t => t.price === STARTER_GRANT_USD) as Tier;
const RESET_DATE = '1 Sep';
export function UsageBillingB() {
  const [state, setState] = useState<AccountState>('starter');
  const [selectedTier, setSelectedTier] = useState<Tier | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const avgDaily = Math.round(DAILY_BURN.reduce((a, b) => a + b, 0) / DAILY_BURN.length);

  // The same drawdown card, with the numbers each state implies.
  const balance = useMemo(() => {
    if (state === 'starter') {
      return {
        label: 'Credits remaining',
        total: STARTER_GRANT_CREDITS,
        used: CREDITS_USED,
        caption: `of ${fmt(STARTER_GRANT_CREDITS)} free credits`
      };
    }
    return {
      label: 'Credits remaining this month',
      total: CURRENT_PLAN.credits,
      used: state === 'subscribed' ? Math.round(CURRENT_PLAN.credits * 0.34) : CURRENT_PLAN.credits,
      caption: `of ${fmt(CURRENT_PLAN.credits)} · resets ${RESET_DATE}`
    };
  }, [state]);
  const remaining = Math.max(0, balance.total - balance.used);
  const daysLeft = Math.max(1, Math.round(remaining / avgDaily));
  const isOut = remaining === 0;
  const isUpgrade = selectedTier ? selectedTier.credits > CURRENT_PLAN.credits : false;

  // Heading, explanation and button label all follow the state.
  const heading = state === 'starter' ? 'When your credit runs out' : state === 'subscribed' ? 'Your plan' : 'Change your plan';
  const explainer = state === 'starter' ? `Your ${fmt(STARTER_GRANT_CREDITS)} starter credits are worth ${price(STARTER_GRANT_USD)} and yours to use — pick a monthly plan for when they run out. Nothing is charged until then.` : state === 'subscribed' ? `${fmt(CURRENT_PLAN.credits)} credits for ${price(CURRENT_PLAN.price)}/mo · month 2 · renews ${RESET_DATE}. Shared across your ${WORKSPACE_PEOPLE}-person workspace.` : `You've used all ${fmt(CURRENT_PLAN.credits)} credits before the ${RESET_DATE} reset. Move up to resume straight away, or move down and it takes effect on ${RESET_DATE}.`;
  const ctaLabel = state === 'starter' ? 'Start subscription' : !selectedTier ? 'Change plan' : isUpgrade ? state === 'depleted' ? 'Upgrade and resume' : 'Upgrade plan' : 'Schedule downgrade';
  return <div data-id="element-1549" data-mp="1m0svr2j2f2or">
      {/* Demo control — which account state to show */}
      <div className="mb-4 flex flex-wrap items-center gap-2" data-id="element-1550" data-mp="oklq65gs2ena">
        <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-400" data-id="element-1551" data-mp="oi9cw5lyssgk">
          State
        </span>
        <div className="flex items-center gap-0.5 rounded-lg bg-neutral-100 p-0.5" data-id="element-1552" data-mp="2v8m4jz133786">
          {STATES.map(s => <button key={s.value} type="button" onClick={() => {
          setState(s.value);
          setSelectedTier(null);
          setPickerOpen(false);
        }} aria-pressed={state === s.value} className={`rounded-md px-2.5 py-1 text-[12px] font-semibold transition-colors ${state === s.value ? 'bg-white text-[#2B211A] shadow-sm' : 'text-neutral-500 hover:text-neutral-700'}`} data-id="element-1553" data-mp="2q9jcdf67cvhm">
              {s.label}
            </button>)}
        </div>
      </div>

      {/* 1 · How much is left */}
      <section className={`rounded-xl border p-5 ${isOut ? 'border-red-200 bg-red-50/40' : state === 'starter' ? 'border-[#EA580C]/30 bg-[#FFF7ED]' : 'border-neutral-200'}`} data-id="element-1554" data-mp="3nx9uw90owmsl">
        <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-1555" data-mp="3iyzw7ofrb428">
          <p className={`text-[13px] font-semibold ${isOut ? 'text-red-700' : 'text-neutral-500'}`} data-id="element-1556" data-mp="1tk1xc9lbxx39">
            {balance.label}
          </p>
          {state === 'starter' && <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EA580C] px-2.5 py-1 text-[11.5px] font-bold text-white" data-id="element-1557" data-mp="41lhunimdwd5">
              <Gift size={12} data-id="element-1558" data-mp="90oo2bac68mp" />
              {price(STARTER_GRANT_USD)} on us
            </span>}
        </div>
        <p className="mt-1 flex flex-wrap items-baseline gap-x-2" data-id="element-1559" data-mp="3qhfyugpb0d95">
          <span className={`text-[32px] font-bold leading-none ${isOut ? 'text-red-700' : 'text-[#2B211A]'}`} data-id="element-1560" data-mp="3vfr7io3v4hps">
            {fmt(remaining)}
          </span>
          <span className="text-[15px] font-medium text-neutral-500" data-id="element-1561" data-mp="3hjdz04hb3qng">
            {balance.caption}
          </span>
        </p>

        <div role="progressbar" aria-valuenow={balance.used} aria-valuemin={0} aria-valuemax={balance.total} aria-label="Credits used" className={`mt-4 h-2 w-full overflow-hidden rounded-full ${state === 'starter' ? 'bg-[#EA580C]/15' : 'bg-neutral-100'}`} data-id="element-1562" data-mp="37jdisreqhqnh">
          <motion.div initial={{
          width: 0
        }} animate={{
          width: `${Math.round(balance.used / balance.total * 100)}%`
        }} transition={{
          type: 'spring',
          stiffness: 120,
          damping: 20
        }} className={`h-full rounded-full ${isOut ? 'bg-red-500' : state === 'starter' ? 'bg-[#EA580C]' : 'bg-[#2B211A]'}`} data-id="element-1563" data-mp="15pe8a89frdbu" />
        </div>

        <div className="mt-4 grid grid-cols-3 gap-4 border-t border-neutral-100 pt-4" data-id="element-1564" data-mp="zbidy85pap8t">
          <Fact label="Used so far" value={`${fmt(balance.used)} credits`} data-id="element-1565" data-mp="vb7uptswe2c0" />
          <Fact label="Daily average" value={`${fmt(avgDaily)} credits`} data-id="element-1566" data-mp="1ryunnhqrj2kp" />
          <Fact label="At this pace" value={isOut ? 'Work is paused' : `~${daysLeft} days left`} data-id="element-1567" data-mp="1n12z8xpbtyl9" />
        </div>
      </section>

      {/* 2 · The decision */}
      <section className="mt-5 rounded-xl border border-neutral-200 p-5" data-id="element-1568" data-mp="1i89ui9tuyq96">
        <h3 className="text-[15px] font-semibold text-[#2B211A]" data-id="element-1569" data-mp="1e7pv0zltbajv">{heading}</h3>
        <p className="mt-1 text-[13px] leading-relaxed text-neutral-500" data-id="element-1570" data-mp="2aqng9b1x6lrw">
          {explainer}
        </p>

        <div className="mt-4" data-id="element-1571" data-mp="25ym8a1bsbh7o">
          <button type="button" onClick={() => setPickerOpen(o => !o)} aria-haspopup="listbox" aria-expanded={pickerOpen} className={`flex w-full items-center justify-between gap-3 rounded-xl border bg-white px-4 py-3 text-left transition-shadow ${pickerOpen ? 'border-[#EA580C] ring-2 ring-[#EA580C]/25' : 'border-neutral-300 hover:border-neutral-400'}`} data-id="element-1572" data-mp="25ydwkcreehy2">
            <span className={`text-[15px] font-medium ${selectedTier ? 'text-[#2B211A]' : 'text-neutral-400'}`} data-id="element-1573" data-mp="6h2bqznrm75a">
              {selectedTier ? `${fmt(selectedTier.credits)} credits · ${price(selectedTier.price)}/mo` : state === 'starter' ? 'Choose a monthly plan' : 'Choose a different plan'}
            </span>
            <ChevronUp size={18} className={`shrink-0 text-neutral-400 transition-transform ${pickerOpen ? '' : 'rotate-180'}`} data-id="element-1574" data-mp="l6rhz9apt1cf" />
          </button>

          {/* Inline list — expands in place so it is never clipped. */}
          <AnimatePresence initial={false} data-id="element-1575" data-mp="u87sdh1j53gn">
            {pickerOpen && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.2
          }} className="overflow-hidden" data-id="element-1576" data-mp="z4og4a4thft7">
                <ul role="listbox" aria-label="Monthly plan" className="mt-2 max-h-60 overflow-y-auto rounded-xl border border-neutral-200 py-1.5" data-id="element-1577" data-mp="j1peut7023yp">
                  {PRICING_DATA.map((item, idx) => {
                if ('group' in item) {
                  return <li key={`g-${idx}`} className="px-4 pb-1 pt-3 text-[11px] font-bold uppercase tracking-wider text-neutral-400" data-id="element-1578" data-mp="3ii462bwwhawu">
                          {item.group}
                        </li>;
                }
                // Only the plan already in force is unselectable — moving down
                // is allowed and simply takes effect at the next reset.
                const isCurrent = state !== 'starter' && item.price === CURRENT_PLAN.price;
                const active = selectedTier?.price === item.price;
                return <li key={item.price} role="option" aria-selected={active} data-id="element-1579" data-mp="2pnfv9npvk9nx" data-mpi={idx}>
                        <button type="button" disabled={isCurrent} onClick={() => {
                    setSelectedTier(item);
                    setPickerOpen(false);
                  }} className={`flex w-full items-center gap-2.5 px-4 py-2.5 text-left transition-colors ${isCurrent ? 'cursor-default' : 'hover:bg-neutral-50'}`} data-id="element-1580" data-mp="2uext42wlpu4o">
                          <span className={`flex-1 text-[14.5px] ${isCurrent ? 'text-neutral-400' : 'text-[#2B211A]'} ${active ? 'font-bold' : 'font-medium'}`} data-id="element-1581" data-mp="38fibwz2inpy6">
                            {fmt(item.credits)} credits monthly
                          </span>
                          {isCurrent && <span className="shrink-0 rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-bold text-neutral-500" data-id="element-1582" data-mp="33eb4x2876usk">
                              Current plan
                            </span>}
                          {item.popular && !isCurrent && <span className="shrink-0 rounded-full bg-[#FBEAE0] px-2 py-0.5 text-[11px] font-bold text-[#EA580C]" data-id="element-1583" data-mp="1s18pwblqydai">
                              Most popular
                            </span>}
                          {active && <Check size={15} className="shrink-0 text-[#EA580C]" data-id="element-1584" data-mp="20alb1r7lyzdc" />}
                          <span className="w-16 shrink-0 text-right text-[14.5px] font-medium text-neutral-500" data-id="element-1585" data-mp="1ljkv714j8376">
                            {price(item.price)}
                          </span>
                        </button>
                      </li>;
              })}
                </ul>
              </motion.div>}
          </AnimatePresence>
        </div>

        {selectedTier && <p className="mt-3 text-[13px] leading-relaxed text-neutral-500" data-id="element-1586" data-mp="12m1qg9p3x6qk">
            {state === 'starter' ? <>
                After your starter credit,{' '}
                <span className="font-semibold text-[#2B211A]" data-id="element-1587" data-mp="2wz9ixupm1691">
                  {price(selectedTier.price)}/mo
                </span>{' '}
                for {fmt(selectedTier.credits)} credits. Cancel anytime.
              </> : <>
                <span className="font-semibold text-[#2B211A]" data-id="element-1588" data-mp="qbixnxlnb0y2">
                  {price(selectedTier.price)}/mo
                </span>{' '}
                for {fmt(selectedTier.credits)} credits,{' '}
                {isUpgrade ? 'up' : 'down'} from {price(CURRENT_PLAN.price)}.{' '}
                {isUpgrade && state === 'depleted' ? `Charged today, prorated for the rest of this cycle, with credits available straight away.` : `Takes effect on ${RESET_DATE} — your current credits stay as they are until then.`}
              </>}
          </p>}

        <button type="button" disabled={!selectedTier} className={`mt-4 inline-flex items-center gap-2 rounded-xl px-5 py-3 text-[15px] font-bold transition-colors ${selectedTier ? 'bg-[#EA580C] text-white shadow-[0_12px_30px_-10px_rgba(234,88,12,0.6)] hover:bg-[#d24e08]' : 'cursor-not-allowed bg-neutral-100 text-neutral-400'}`} data-id="element-1589" data-mp="1izepop09s4oi">
          {ctaLabel}
          <ArrowRight size={15} data-id="element-1590" data-mp="3ft00cde4eed7" />
        </button>
      </section>

      {/* 3 · Where it went */}
      <section className="mt-5" data-id="element-1591" data-mp="1jpvah26ns65a">
        <h3 className="text-[15px] font-semibold text-[#2B211A]" data-id="element-1592" data-mp="1jqwo9ef276qp">
          Where the credits went
        </h3>
        <ul className="mt-2.5 divide-y divide-neutral-100 rounded-xl border border-neutral-200" data-id="element-1593" data-mp="1sp8az5ct1kdv">
          {CONSUMPTION_MIX.map(row => <li key={row.label} className="flex items-center gap-4 px-4 py-2.5" data-id="element-1594" data-mp="1xol9ind1gfwa">
              <span className="flex-1 text-[14px] font-medium text-[#2B211A]" data-id="element-1595" data-mp="22no2h9fquvnw">
                {row.label}
              </span>
              <span className="hidden h-1.5 w-28 overflow-hidden rounded-full bg-neutral-100 sm:block" data-id="element-1596" data-mp="27lh923kylrop">
                <span className="block h-full rounded-full bg-[#2B211A]" style={{
              width: `${Math.round(row.share / 0.41 * 100)}%`
            }} data-id="element-1597" data-mp="2ceag4y3whc3y" />
              </span>
              <span className="w-24 shrink-0 text-right text-[13px] text-neutral-500" data-id="element-1598" data-mp="2lep09u54ol5v">
                <span className="font-semibold text-[#2B211A]" data-id="element-1599" data-mp="2qcqsb4kj7uth">
                  {fmt(Math.round(balance.used * row.share))}
                </span>{' '}
                · {Math.round(row.share * 100)}%
              </span>
            </li>)}
        </ul>
      </section>
    </div>;
}
function Fact({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div data-id="element-1600" data-mp="2qku549gktlpv">
      <p className="text-[12px] text-neutral-500" data-id="element-1601" data-mp="2qkkp9ep8wuxw">{label}</p>
      <p className="mt-0.5 text-[14px] font-bold text-[#2B211A]" data-id="element-1602" data-mp="2mihb2ib6zc8x">{value}</p>
    </div>;
}